import Head from 'next/head';
import {
  ComponentRendering,
  Field,
  LayoutServiceData,
  RouteData,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { findFieldsByComponentName } from './findFieldsByComponentName';
import {
  BLOG_DETAILS_COMPONENT_NAME,
  BLOG_PAGE_TEMPLATES,
  NEWS_DETAILS_COMPONENT_NAME,
  NEWS_PAGE_TEMPLATE_ID,
} from 'src/shared/static';

// Types
import { ArticleFields } from 'src/shared/ArticleBase.types';
import { LayoutServiceDataExtensions } from './shared.types';

export const SearchMeta = (props?: SearchMetaProps) => {
  const { fields, route, layoutData, publicUrl } = props || {};

  const isNewsPage = route?.templateId === NEWS_PAGE_TEMPLATE_ID;
  const isBlogPage = BLOG_PAGE_TEMPLATES.includes(route?.templateId || '');

  const isNewsorBlogPage = isNewsPage ? 'News Article' : isBlogPage ? 'Blog' : 'Webpage';
  const pageCategory = fields?.Category?.value || isNewsorBlogPage;
  const nearestHomepage = layoutData?.sitecore?.context?.nearestHomepage?.value;

  const mainPlaceholderRenderings =
    (route?.placeholders?.['headless-main'] as ComponentRendering[]) || [];
  const articleFields = findFieldsByComponentName<ArticleFields>(
    mainPlaceholderRenderings,
    isNewsPage ? NEWS_DETAILS_COMPONENT_NAME : BLOG_DETAILS_COMPONENT_NAME
  );

  const isNewsOrBlog = isNewsPage || isBlogPage;
  const excludeFromSiteSearch = fields?.ExcludeFromSearch?.value ? 'true' : 'false';
  const shouldRenderImageMeta = isNewsOrBlog && articleFields?.FeaturedImage?.value?.src;
  const shouldRenderPublishDate = isNewsOrBlog && articleFields?.PublishDate?.value;
  const searchHeading = fields?.SearchResultHeading?.value;
  const searchDescription = fields?.SearchResultDescription?.value;
  const articleType = isNewsPage ? 'news' : 'blog';
  const isArticleFeatured = fields?.IsFeatured?.value ? 'true' : 'false';
  const publishedDate = articleFields?.PublishDate?.value;
  const featuredImageUrl = `${publicUrl}${articleFields?.FeaturedImage?.value?.src}`;

  return (
    <Head>
      <meta className="elastic" name="exclude_from_search" content={excludeFromSiteSearch} />
      <meta className="elastic" name="category" content={pageCategory} />
      <meta className="elastic" name="section" content={nearestHomepage} />
      {searchHeading && <meta className="elastic" name="search_heading" content={searchHeading} />}
      {searchDescription && (
        <meta className="elastic" name="search_description" content={searchDescription} />
      )}
      {isNewsOrBlog && <meta className="elastic" name="article_type" content={articleType} />}
      {isNewsOrBlog && (
        <meta className="elastic" name="article_is_featured" content={isArticleFeatured} />
      )}
      {shouldRenderPublishDate && (
        <meta className="elastic" name="article_publish_date" content={publishedDate} />
      )}
      {shouldRenderImageMeta && (
        <meta className="elastic" name="featured_image_url" content={featuredImageUrl} />
      )}
    </Head>
  );
};

export interface SearchMetaFields {
  ExcludeFromSearch?: Field<string>;
  SearchResultHeading?: Field<string>;
  SearchResultDescription?: Field<string>;
  Category?: Field<string>;
  IsFeatured?: Field<string>;
}

export type SearchMetaProps = {
  fields?: SearchMetaFields;
  route?: RouteData;
  layoutData?: LayoutServiceData & { sitecore: LayoutServiceDataExtensions };
  publicUrl?: string;
};
